import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import normalizeImage from "../utils/normalizeImage";

/**
 * Shared large camera modal — a live preview, a framing guide and a 3-second
 * self-timer, with a file-upload fallback on every path. Used for BOTH the
 * driver-licence capture (variant "card", rear camera, card-shaped guide) and
 * the FOXID face-match selfie (variant "selfie", front camera, mirrored preview
 * with a face oval). Kept in one place so the two stay visually consistent.
 *
 *   variant:   "card" | "selfie"
 *   title:     modal heading (e.g. "Front of licence" / "Selfie")
 *   hint:      one-line instruction under the heading
 *   onCapture: (blob) => void   — the captured JPEG
 *   onClose:   () => void
 */
export default function CameraCapture({ variant = "card", title, hint, onCapture, onClose }) {
  const isSelfie = variant === "selfie";
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const timerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [count, setCount] = useState(0);
  const [err, setErr] = useState("");

  const stopStream = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);
  const cancelTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setCount(0);
  }, []);

  // Open the camera on mount; stop it on unmount so the camera light never lingers.
  useEffect(() => {
    let cancelled = false;
    setErr(""); setReady(false);
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("unsupported");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: isSelfie ? "user" : "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setReady(true);
      } catch (e) {
        setErr(e?.name === "NotAllowedError"
          ? "Camera access was blocked. Allow it in your browser, or upload a photo instead."
          : "Couldn't open the camera. Upload a photo instead.");
      }
    })();
    return () => { cancelled = true; cancelTimer(); stopStream(); };
    // eslint-disable-next-line
  }, []);

  // Escape closes the modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const snap = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    // Draw the true (un-mirrored) frame even though the selfie PREVIEW is
    // mirrored for a natural feel — FOXID needs the real orientation.
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => { if (blob) { cancelTimer(); stopStream(); onCapture(blob); } }, "image/jpeg", 0.92);
  }, [cancelTimer, stopStream, onCapture]);

  const startTimer = () => {
    if (count > 0) return;
    let n = 3;
    setCount(n);
    timerRef.current = setInterval(() => {
      n -= 1;
      if (n <= 0) { clearInterval(timerRef.current); timerRef.current = null; setCount(0); snap(); }
      else setCount(n);
    }, 1000);
  };

  const onFile = async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { stopStream(); onCapture(await normalizeImage(f)); } };

  return createPortal(
    <div className="su-cam-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="su-cam-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="su-cam-head">
          <div>
            <div className="su-cam-title">{title}</div>
            <div className="su-cam-hint">{hint}</div>
          </div>
          <button type="button" className="su-cam-close" onClick={onClose} aria-label="Close camera">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className={`su-cam-stage${isSelfie ? " is-selfie" : ""}`}>
          <video ref={videoRef} autoPlay muted playsInline />
          {ready && !err && <div className={`su-cam-guide${isSelfie ? " is-face" : ""}`} />}
          {count > 0 && <div className="su-cam-count">{count}</div>}
          {err && (
            <div className="su-cam-err">
              <p>{err}</p>
              <button type="button" className="su-btn su-btn-primary" onClick={() => fileRef.current?.click()}>Upload a photo</button>
            </div>
          )}
        </div>

        <div className="su-cam-actions">
          {ready && !err && (count > 0 ? (
            <button type="button" className="su-cam-shutter is-counting" onClick={cancelTimer}>Cancel</button>
          ) : (
            <button type="button" className="su-cam-shutter" onClick={startTimer}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 7 12 12 15 14" /></svg>
              Capture (3s)
            </button>
          ))}
          {!err && (
            <button type="button" className="su-cam-upload" onClick={() => fileRef.current?.click()}>Upload a photo instead</button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} />
      </div>
    </div>,
    document.body
  );
}
